import { useMutation } from '@apollo/client';
import { useCallback, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
    Body1,
    Button,
    Modal,
    Stack,
} from '@myphx-engineering/phx-design-system';
import { useDisclosure } from '@myphx-engineering/phx-design-system/hooks';

import { useFillPathway } from '../../../../contexts/fill/use-fill-prescription';
import { RequestPrescriberOrderModificationDocument } from '../../../../graphql/generated/graphql';
import { usePatientContext } from '../../../../hooks/use-patient-context';
import { useRefetchQueries } from '../../../../hooks/use-refetch-queries';
import type {
    NdcSwitchTargetV2,
    SwitchRequestSource,
} from '../../../../types/drug-config';
import { getAbsoluteRoute } from '../../../../util/get-absolute-route';
import { getRefetchQueryForPrescription } from '../../../../util/get-refetch-query-for-prescription';
import { usePatientChoiceMutation } from '../common/use-patient-choice-mutation';

import { useKeepCurrent } from './use-keep-current';

export const SwitchRequestButton = ({
    drxId,
    onLoadingChange,
    requestSource,
    switchTarget,
    'data-testid': testId,
}: {
    drxId: string;
    onLoadingChange?: (isLoading: boolean) => void;
    requestSource: SwitchRequestSource;
    switchTarget: NdcSwitchTargetV2 | null;
    'data-testid'?: string;
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { patient } = usePatientContext();

    const [isModalOpen, { open, close }] = useDisclosure(false);
    const { pathway, originalPathway, subPathway } = useFillPathway();

    const savePatientChoices = usePatientChoiceMutation({
        drxId,
    });

    const [requestModification, { loading: isMutationLoading }] = useMutation(
        RequestPrescriberOrderModificationDocument
    );

    const {
        refetch,
        isComplete: isRefetchComplete,
        isLoading: isRefetchLoading,
    } = useRefetchQueries(getRefetchQueryForPrescription(drxId, patient.id));

    useEffect(() => {
        if (isRefetchComplete) {
            navigate({
                pathname: getAbsoluteRoute('fill.drxId.onHold', { drxId }),
            });
        }
    }, [isRefetchComplete, navigate, drxId]);

    useEffect(() => {
        onLoadingChange?.(isMutationLoading || isRefetchLoading);
    }, [isMutationLoading, isRefetchLoading, onLoadingChange]);

    const handleRequest = useCallback(async () => {
        close();

        savePatientChoices({ pathway, originalPathway, subPathway });

        const result = await requestModification({
            variables: {
                prescriberOrderId: drxId,
                ndc: switchTarget?.ndc,
                putPrescriptionOnHold: true,
                changeRequestReason: subPathway ?? undefined,
            },
        });

        if (result.data?.requestPrescriberOrderModification.success) {
            void refetch();
        }
    }, [
        close,
        drxId,
        originalPathway,
        pathway,
        refetch,
        requestModification,
        savePatientChoices,
        subPathway,
        switchTarget,
    ]);

    const handleButtonClick = useCallback(() => {
        if (requestSource.requestSwitch?.modal) {
            open();
        } else {
            void handleRequest();
        }
    }, [handleRequest, open, requestSource.requestSwitch?.modal]);

    const handleKeepCurrent = useKeepCurrent(drxId, requestSource);

    const confirmationModal = requestSource.requestSwitch?.modal;

    return (
        <>
            <Button
                disabled={!switchTarget}
                size="lg"
                loading={isMutationLoading || isRefetchLoading}
                onClick={handleButtonClick}
                data-testid={testId}
            >
                {switchTarget
                    ? t('fill.switch.cta.submitRequest', {
                          name: switchTarget.description,
                      })
                    : t('fill.switch.cta.selectForm')}
            </Button>

            {confirmationModal && switchTarget && (
                <Modal
                    opened={isModalOpen}
                    onClose={close}
                    title={confirmationModal.title}
                >
                    <Stack gap="xl">
                        <Stack gap="xs">
                            {confirmationModal.description.map((line) => (
                                <Body1 key={line}>
                                    <Trans
                                        ns="dynamic"
                                        components={{ b: <strong /> }}
                                    >
                                        {line}
                                    </Trans>
                                </Body1>
                            ))}
                        </Stack>
                        <Stack>
                            <Button
                                loading={isMutationLoading || isRefetchLoading}
                                onClick={() => void handleRequest()}
                            >
                                {t('fill.switch.cta.submitRequest', {
                                    name: switchTarget.description,
                                })}
                            </Button>
                            <Button
                                variant="outline"
                                disabled={isMutationLoading || isRefetchLoading}
                                onClick={handleKeepCurrent}
                            >
                                {t('fill.switch.cta.keepCurrent')}
                            </Button>
                        </Stack>
                    </Stack>
                </Modal>
            )}
        </>
    );
};
